import { formatPriceLak } from "@/shared/lib/format-price";
import { format } from "date-fns";
import React from "react";
import logo from "../../../../../assets/favicon_io/android-chrome-192x192.png";
import {
  buildPrintCarrierDisplay,
  formatPrintBillLocationLine,
  formatPrintVariantOptions,
  type OrderPrintData,
} from "../../utils/print-types";
import { formatOrderPaymentMethodLabel } from "../../utils/order-retail-display";

interface ReceiptPrintViewProps {
  order: OrderPrintData;
}

// Brand configuration - can be moved to settings later
const BRAND_NAME = "Freedom";
const BRAND_TAGLINE = "Thank you / Hope to see you again";
// Use absolute path for logo in print window (favicon_io)
const BRAND_LOGO = logo;

export function ReceiptPrintView({ order }: ReceiptPrintViewProps) {
  const carrierLine = buildPrintCarrierDisplay(order.shipping);
  const storePhone = order.contactPhone?.trim();

  const items = order.items.map((item, idx) => ({
    index: idx + 1,
    name: item.productName ?? "-",
    variant: formatPrintVariantOptions(item.resolvedVariantOptions),
    quantity: item.quantity,
    unitPrice: Number(item.variantPrice || item.price),
    total: item.total,
  }));

  const subtotal = order.items.reduce((sum, item) => sum + item.total, 0);
  const shipping = order.shippingFee ?? 0;
  const discount = order.discountTotal ?? 0;
  const total = order.grandTotal;

  return (
    <div className="print-container receipt">
      <div className="print-header">
        <div className="brand-header-row">
          <div className="brand-logo">
            <img
              src={BRAND_LOGO}
              alt={BRAND_NAME}
              className="logo-image"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <div className="brand-text">
            <div className="brand-by">{BRAND_NAME}</div>
            <div className="brand-tagline">{BRAND_TAGLINE}</div>
          </div>
        </div>
      </div>
      {storePhone ? (
        <>
          <div className="print-divider" />
          <div className="print-section">
            <div className="contact-info">Call: {storePhone}</div>
          </div>
        </>
      ) : null}
      <div className="print-divider" />
      <div className="print-section">
        <div className="label-field">
          <span className="label-text">Order:</span>
          <span className="label-value">{order.orderNumber}</span>
        </div>
        <div className="label-field">
          <span className="label-text">Date:</span>
          <span className="label-value">
            {format(new Date(order.createdAt), "dd/MM/yyyy HH:mm")}
          </span>
        </div>
        <div className="label-field">
          <span className="label-text">Customer:</span>
          <span className="label-value">{order.customer?.name ?? "-"}</span>
        </div>
        <div className="label-field">
          <span className="label-text">Phone:</span>
          <span className="label-value">
            {order.customer?.phoneNumber ?? "-"}
          </span>
        </div>
        {(() => {
          const loc = formatPrintBillLocationLine(order.shipping);
          return loc ? (
            <div className="label-field">
              <span className="label-text">Address:</span>
              <span className="label-value">{loc}</span>
            </div>
          ) : null;
        })()}
        {carrierLine ? (
          <div className="label-field">
            <span className="label-text">Carrier:</span>
            <span className="label-value">{carrierLine}</span>
          </div>
        ) : null}
      </div>
      <div className="print-divider" />
      <div className="print-section">
        <div className="section-title">Items:</div>
        <table className="items-table">
          <tbody>
            {items.map((item) => (
              <React.Fragment key={item.index}>
                <tr>
                  <td colSpan={2}>
                    {item.index}. {item.name}
                  </td>
                </tr>
                {item.variant && (
                  <tr>
                    <td colSpan={2} style={{ paddingLeft: "1em" }}>
                      {item.variant}
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ paddingLeft: "1em" }}>
                    Qty: {item.quantity} x {formatPriceLak(item.unitPrice)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatPriceLak(item.total)}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="print-divider" />
      <div className="print-section">
        <div className="total-row">
          <span>Subtotal:</span>
          <span>{formatPriceLak(subtotal)}</span>
        </div>
        {shipping > 0 && (
          <div className="total-row">
            <span>Shipping:</span>
            <span>{formatPriceLak(shipping)}</span>
          </div>
        )}
        {discount > 0 && (
          <div className="total-row">
            <span>Discount:</span>
            <span>-{formatPriceLak(discount)}</span>
          </div>
        )}
        <div className="total-row total">
          <span>Total:</span>
          <span>{formatPriceLak(total)}</span>
        </div>
      </div>
      <div className="print-divider" />
      <div className="print-section">
        <div className="label-field">
          <span className="label-text">Payment:</span>
          <span className="label-value">
            {formatOrderPaymentMethodLabel(
              order.paymentMethod,
              order.channel,
            )}
          </span>
        </div>
        <div className="label-field">
          <span className="label-text">Status:</span>
          <span className="label-value">{order.paymentStatus}</span>
        </div>
      </div>
      <div className="print-divider" />
      <div className="print-footer">ຂອບໃຈທີ່ໃຊ້ບໍລິການຂອງພວກເຮົາ</div>
    </div>
  );
}
